/**
 * The privacy policy: what Morcast collects, what it publishes, who helps run it, how long it
 * keeps data and what people can do about it. Google's verification of the YouTube scopes and the
 * YouTube API Services policies require it to be public and easy to reach, so the footer of every
 * page links it, and so does the page where creators link their accounts.
 *
 * Every statement follows what the code does. A change to what Morcast collects, keeps, publishes
 * or shares changes this page too, with a new date in `PRIVACY_UPDATED`.
 */

import type { ReactNode } from "react";
import { Link } from "react-router";

import { PageHeader, styles } from "../components/ui.js";
import { useAppConfig } from "../config.js";

/** The date of this version, shown at the top of the page. */
export const PRIVACY_UPDATED = "14 October 2026";

/** Pages this policy points to outside Morcast. */
export const POLICY_LINKS = {
  youtubeTerms: "https://www.youtube.com/t/terms",
  googlePrivacy: "https://policies.google.com/privacy",
  googleUserData: "https://developers.google.com/terms/api-services-user-data-policy",
  googlePermissions: "https://security.google.com/settings/security/permissions",
  xConnectedApps: "https://x.com/settings/connected_apps",
} as const;

/** The policy's sections, in order, for the contents and the page. */
const SECTIONS = [
  { id: "summary", title: "In short" },
  { id: "who", title: "Who we are" },
  { id: "collect", title: "What we collect" },
  { id: "use", title: "How we use it" },
  { id: "public", title: "What becomes public" },
  { id: "youtube", title: "YouTube and Google data" },
  { id: "x", title: "X data" },
  { id: "sharing", title: "Who we share it with" },
  { id: "keeping", title: "How long we keep it" },
  { id: "choices", title: "Your choices and rights" },
  { id: "security", title: "Security" },
  { id: "children", title: "Children" },
  { id: "changes", title: "Changes to this policy" },
] as const;

type SectionId = (typeof SECTIONS)[number]["id"];

const paragraph = "mt-4 leading-7 text-zinc-700 dark:text-zinc-300";
const list =
  "mt-4 list-disc space-y-2.5 pl-5 leading-7 text-zinc-700 marker:text-zinc-400 dark:text-zinc-300 dark:marker:text-zinc-500";
const subheading = "mt-6 font-semibold text-zinc-950 dark:text-white";
/** An OAuth scope's name, kept on one line. */
const scope = "font-mono text-sm whitespace-nowrap";

export function Privacy() {
  return (
    <div className={styles.narrowPage}>
      <PageHeader eyebrow="Legal" title="Privacy policy">
        Last updated {PRIVACY_UPDATED}. This policy explains what Morcast collects, what it
        publishes, who helps run it and what you can do about your data. It covers the Morcast web
        app and the services behind it.
      </PageHeader>

      <nav aria-label="Contents" className={`mt-8 ${styles.card}`}>
        <h2 className="text-xs font-semibold tracking-[0.12em] text-zinc-500 uppercase dark:text-zinc-400">
          Contents
        </h2>
        <ol className="mt-4 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
          {SECTIONS.map((section, index) => (
            <li key={section.id}>
              <a href={`#${section.id}`} className={styles.link}>
                {index + 1}. {section.title}
              </a>
            </li>
          ))}
        </ol>
      </nav>

      <div className="mt-4 divide-y divide-zinc-900/[0.07] dark:divide-white/[0.07]">
        <PolicySection id="summary">
          <ul className={list}>
            <li>
              You sign in with a wallet. There is no password, and Morcast does not ask for your
              name.
            </li>
            <li>
              Campaign results are public and permanent. They list wallets, accounts by their ID,
              the posts and videos that took part and what each one earned.
            </li>
            <li>
              Morcast reads your X and YouTube data only through the access you grant. It can never
              post, and you can take the access back at any time.
            </li>
            <li>We never sell your data, and we never use it for advertising.</li>
            <li>
              Records on the blockchain and copies on Arweave cannot be deleted. Write to us about
              anything else.
            </li>
          </ul>
        </PolicySection>

        <PolicySection id="who">
          <p className={paragraph}>
            Morcast runs pay-for-performance creator campaigns on Base. Brands deposit a budget in
            an escrow contract. Creators publish posts and videos for the campaign. Morcast measures
            and checks them, then publishes the result that pays creators. In this policy,
            "Morcast", "we" and "us" mean the team that operates the app.
          </p>
          <p className={paragraph}>
            Questions about this policy or your data go to <SupportEmail />.
          </p>
        </PolicySection>

        <PolicySection id="collect">
          <h3 className={subheading}>When you sign in</h3>
          <ul className={list}>
            <li>
              Your wallet address. You sign in by signing a message with your wallet, which proves
              you control it and costs nothing.
            </li>
            <li>
              A session cookie that keeps you signed in for up to 12 hours. It holds a random token,
              and we store only a hash of that token with your wallet address.
            </li>
            <li>When your wallet first signed in and when it last signed in.</li>
            <li>
              The notifications we write for your wallet, such as a decision on your request, and
              when you last opened them.
            </li>
          </ul>

          <h3 className={subheading}>If you run campaigns as a brand</h3>
          <ul className={list}>
            <li>
              Your brand's name, a contact email and the terms and brief of each campaign you
              request, with any brand kit images you upload.
            </li>
            <li>Our decisions on your requests and their changes, with any notes.</li>
          </ul>

          <h3 className={subheading}>If you take part as a creator</h3>
          <ul className={list}>
            <li>
              The X accounts and YouTube channels you link. We keep each account's ID, its handle or
              name, and an encrypted token that lets Morcast read its data.
            </li>
            <li>
              The posts and videos you submit, with what X or YouTube reported about them when you
              submitted them. For a YouTube video, also its source file, which you upload.
            </li>
            <li>
              What we work out from an uploaded video. That is its length and picture size, a
              transcript of its speech and still frames from it.
            </li>
            <li>
              Measurements of each item, with the answers from X and YouTube they came from. On X,
              that is organic impressions. On YouTube, that is views without advertising traffic
              and, for a sponsored segment, how much of it viewers watched.
            </li>
            <li>
              To spot fraud, each account's creation date and its usual reach in the 30 days before
              a campaign starts.
            </li>
            <li>The results of our checks, including notes from the admin team's reviews.</li>
            <li>Claims you ask Morcast to send for you, with their transactions.</li>
          </ul>

          <h3 className={subheading}>From everyone</h3>
          <ul className={list}>
            <li>Issues you raise about a published result, and our decisions on them.</li>
            <li>Messages you send us.</li>
            <li>
              Server logs. Our servers log each request with its time, address and outcome and the
              IP address it came from. We use them to run the service and stop abuse.
            </li>
            <li>
              Error reports, when error reporting is on. They include the page address and the steps
              that led to the error. We turn off the collection of IP addresses, cookies and request
              contents.
            </li>
          </ul>

          <h3 className={subheading}>On your device</h3>
          <p className={paragraph}>
            Your browser keeps your theme choice, the deposits you sent from it and your wallet
            connection. This stays on your device, and you can clear it in your browser at any time.
            Morcast uses no tracking or advertising cookies and no analytics.
          </p>
        </PolicySection>

        <PolicySection id="use">
          <ul className={list}>
            <li>To sign you in and keep your session.</li>
            <li>To review brands' requests and run their campaigns.</li>
            <li>
              To check, measure and score the posts and videos creators submit, as each campaign's
              rules describe.
            </li>
            <li>To publish each campaign's result, settle it on the chain and pay creators.</li>
            <li>To spot fraud and keep Morcast secure.</li>
            <li>To answer your messages and fix problems.</li>
          </ul>
          <p className={paragraph}>
            We use your data to provide the service you asked for, to meet our legal duties and for
            our legitimate interest in a secure and fair service. We read your X and YouTube data
            because you linked the account and granted the access.
          </p>
        </PolicySection>

        <PolicySection id="public">
          <p className={paragraph}>
            Morcast is built so that anyone can check its results. These parts are public.
          </p>
          <ul className={list}>
            <li>
              Everything on Base, a public blockchain. Every deposit, settlement and claim is public
              and permanent, with the wallets involved and the amounts.
            </li>
            <li>
              A campaign's manifest, from the moment we accept the brand's request. It holds the
              brand's name and wallet, the campaign's terms and brief and the brand kit images. The
              contact email is never published.
            </li>
            <li>
              A campaign's result, once published. For each item it lists the creator's wallet, the
              account's ID, the address of the post or video, the measured reach, the item's status
              and the reasons for it. The address of an X post shows the account's username. The
              result also lists each creator's payout and the issues raised, with the wallet that
              raised each one.
            </li>
          </ul>
          <p className={paragraph}>
            The manifests of funded campaigns and every result are copied to a public archive on
            Morcast's domain and to Arweave, a permanent public store. Copies there cannot be
            changed or deleted, by us or anyone else.
          </p>
          <p className={paragraph}>
            The evidence behind each item stays private. Only the item's creator and the admin team
            can read it.
          </p>
        </PolicySection>

        <PolicySection id="youtube">
          <p className={paragraph}>
            Morcast uses YouTube API Services. By linking a YouTube channel you agree to the{" "}
            <a href={POLICY_LINKS.youtubeTerms} rel="noopener" className={styles.link}>
              YouTube Terms of Service
            </a>
            . The{" "}
            <a href={POLICY_LINKS.googlePrivacy} rel="noopener" className={styles.link}>
              Google Privacy Policy
            </a>{" "}
            explains how Google handles your data.
          </p>
          <p className={paragraph}>
            When you link a channel, Google asks you to grant two read-only scopes,{" "}
            <code className={scope}>youtube.readonly</code> and{" "}
            <code className={scope}>yt-analytics.readonly</code>. With them Morcast reads the
            following.
          </p>
          <ul className={list}>
            <li>Your channel's ID and name.</li>
            <li>
              The details of each video you submit, and whether it stays public and unchanged during
              the campaign.
            </li>
            <li>
              Each submitted video's views during the campaign without advertising traffic, and for
              a sponsored segment how much of it viewers watched.
            </li>
            <li>
              Your channel's creation date and its daily views in the 30 days before a campaign, to
              spot fraud.
            </li>
          </ul>
          <p className={paragraph}>
            Morcast uses this data only to measure and check the videos you submit to campaigns, and
            to publish their results. It never posts, edits or deletes anything on YouTube. The
            measured views and the video's address appear in the public result. To check a brief's
            requirements, we send a video's title, description, transcript and frames to our model
            provider.
          </p>
          <p className={paragraph}>
            Morcast's use and transfer of information received from Google APIs adheres to the{" "}
            <a href={POLICY_LINKS.googleUserData} rel="noopener" className={styles.link}>
              Google API Services User Data Policy
            </a>
            , including the Limited Use requirements.
          </p>
          <p className={paragraph}>
            You can unlink a channel on the{" "}
            <Link to="/accounts" className={styles.link}>
              Linked accounts
            </Link>{" "}
            page. Morcast then deletes its access token and revokes it with Google. You can also
            remove Morcast's access on{" "}
            <a href={POLICY_LINKS.googlePermissions} rel="noopener" className={styles.link}>
              Google's security settings page
            </a>
            . A video in a campaign not yet settled cannot be measured without the access, and earns
            nothing.
          </p>
        </PolicySection>

        <PolicySection id="x">
          <p className={paragraph}>
            When you link an X account, X asks you to grant read-only access with the scopes{" "}
            <code className={scope}>tweet.read</code>, <code className={scope}>users.read</code> and{" "}
            <code className={scope}>offline.access</code>. Morcast reads your account's ID, username
            and name, the posts you submit with their organic impressions, and whether they stay
            public and unchanged. To spot fraud, it also reads your account's creation date and the
            public metrics of your posts from the 30 days before a campaign. It never posts.
          </p>
          <p className={paragraph}>
            Unlinking the account on the{" "}
            <Link to="/accounts" className={styles.link}>
              Linked accounts
            </Link>{" "}
            page deletes Morcast's token and revokes it with X. You can also remove Morcast in{" "}
            <a href={POLICY_LINKS.xConnectedApps} rel="noopener" className={styles.link}>
              X's connected apps settings
            </a>
            .
          </p>
        </PolicySection>

        <PolicySection id="sharing">
          <p className={paragraph}>
            We share data only with the services that help us run Morcast, each for its own task.
          </p>
          <ul className={list}>
            <li>Railway hosts our services, database, file storage and logs.</li>
            <li>X and Google, when you link an account and when we measure your content.</li>
            <li>
              Our model provider checks posts and videos against the brief. It receives post text,
              image links, video titles, descriptions, transcripts and frames, with the brand's
              brief. It receives no wallet or handle.
            </li>
            <li>Our speech-recognition provider receives the audio of uploaded videos.</li>
            <li>Sentry receives error reports, when error reporting is on.</li>
            <li>
              Our Base RPC provider carries Morcast's reads and transactions on the chain, and
              checks the signatures of smart wallets. Your browser reads the chain through Base's
              public endpoint.
            </li>
            <li>Turbo uploads the public manifests and results to Arweave.</li>
            <li>
              If you sign in with Base Account, Coinbase's Base Account service handles that
              connection. Your wallet's own provider handles any other wallet.
            </li>
          </ul>
          <p className={paragraph}>
            These providers may process data in the United States and other countries. We may also
            disclose data when the law requires it, or to protect Morcast and its users.
          </p>
        </PolicySection>

        <PolicySection id="keeping">
          <ul className={list}>
            <li>Sessions last until they expire or you sign out.</li>
            <li>Notifications are deleted 90 days after they were written.</li>
            <li>
              An uploaded video that is never submitted is deleted after 2 days. The copy of a
              rejected submission's video is deleted at once.
            </li>
            <li>
              A linked account's token is kept until you unlink the account. The record that the
              account was linked stays.
            </li>
            <li>
              Campaign records are kept so that results can be checked and payouts claimed, and
              claims never expire. That covers requests, submissions, measurements, checks,
              evidence, accepted videos with their frames and transcripts, and issues.
            </li>
            <li>Logs are kept for as long as our hosting provider keeps them.</li>
            <li>
              Published manifests and results, their copies on Arweave and everything on the
              blockchain are permanent.
            </li>
          </ul>
        </PolicySection>

        <PolicySection id="choices">
          <ul className={list}>
            <li>Unlink an X account or YouTube channel at any time.</li>
            <li>Sign out at any time, which ends your session.</li>
            <li>Withdraw a submission before its campaign ends.</li>
          </ul>
          <p className={paragraph}>
            You can ask us for a copy of your data, or ask us to correct or delete it, by writing to{" "}
            <SupportEmail />. We answer within 30 days. We cannot delete what is public on Base or
            on Arweave, and we keep the records a published result depends on while it can be
            checked or claimed. Depending on where you live, you may also object to how we use your
            data and complain to your data protection authority.
          </p>
        </PolicySection>

        <PolicySection id="security">
          <p className={paragraph}>
            Everything travels over HTTPS. Account tokens are encrypted before they are stored,
            sessions use a cookie that scripts cannot read, and access to records is limited to the
            people who need it. The keys of Morcast's escrow wallets never reach our servers.
          </p>
        </PolicySection>

        <PolicySection id="children">
          <p className={paragraph}>
            Morcast is for people 18 and older. We do not knowingly collect data from anyone
            younger.
          </p>
        </PolicySection>

        <PolicySection id="changes">
          <p className={paragraph}>
            When this policy changes, we update this page and the date at its top. For a change that
            matters to how we use your data, we also say so in the app before it applies.
          </p>
        </PolicySection>
      </div>
    </div>
  );
}

/** One section of the policy, with its heading and an anchor the contents link to. */
function PolicySection({ id, children }: { id: SectionId; children: ReactNode }) {
  const title = SECTIONS.find((section) => section.id === id)?.title;
  return (
    <section id={id} aria-labelledby={`${id}-title`} className="scroll-mt-24 py-10">
      <h2
        id={`${id}-title`}
        className="font-display text-xl font-semibold tracking-[-0.015em] text-zinc-950 dark:text-white"
      >
        {title}
      </h2>
      {children}
    </section>
  );
}

/** Where people write to Morcast, once the API publishes the address. */
function SupportEmail() {
  const { supportEmail } = useAppConfig();
  if (!supportEmail) {
    return <>the Morcast team</>;
  }
  return (
    <a href={`mailto:${supportEmail}`} className={styles.link}>
      {supportEmail}
    </a>
  );
}
